import { ReactNode, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { Group, Text, Box, Flex } from '@mantine/core';

interface SectionProps {
  title: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
}

// Section chevron — mirrors the canonical primary-nav collapsible-group
// chevron pattern (everself-design-system → primary-nav → "Collapsible-group
// chevron"). Single chevron-down, 14px, 0.55 opacity, rotates between
// -90deg (collapsed) and 0deg (open) over 200ms. Right-anchored so the
// title gets the left-edge scan position.
const CHEVRON_STYLE: React.CSSProperties = {
  opacity: 0.55,
  transition: 'transform 0.2s ease',
  flexShrink: 0,
};

export function Section({ title, icon, defaultOpen = true, collapsible = true, headerAction, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const handleClick = collapsible ? () => setOpen((v) => !v) : undefined;

  return (
    <Box mb="lg">
      <Group
        gap={8}
        align="center"
        justify="space-between"
        mb={12}
        style={{ cursor: collapsible ? 'pointer' : 'default' }}
        onClick={handleClick}
      >
        <Group gap={8} align="center" wrap="nowrap">
          {icon && <Box mr={4}>{icon}</Box>}
          <Text variant="header" fw={600} size="14px">
            {title}
          </Text>
        </Group>
        <Group gap={6} align="center" wrap="nowrap">
          {headerAction && (!collapsible || open) && <Box onClick={(e) => e.stopPropagation()}>{headerAction}</Box>}
          {collapsible && (
            <IconChevronDown
              size={14}
              stroke={1.5}
              style={{
                ...CHEVRON_STYLE,
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            />
          )}
        </Group>
      </Group>
      {(collapsible ? open : true) && (
        <Flex direction="column" gap={8}>
          {children}
        </Flex>
      )}
    </Box>
  );
}
